import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import ImageComponent from "../../common/ImageComponent";
import "../../../pages/course/Courses.css";
import { NEW_COURSE_DETAILS, NEW_COURSE_DETAIL_PAGE } from "../../../constants/RoutesUrl";
import { _imagesURL } from "../../../constants/API";
import { apiAdmin } from "../../../services/api";
import { Language } from "../../../App";
import { useCustomToast } from "../../common/CustomToast";
import Avatar from "../../../assets/images/icon/placeholder_Awatar.png";
import MixpanelManager from "../../../constants/MixpanelManager";
import { MixpanelEvents } from "../../../constants/MixpanelEvents";
import {
  MdOutlinePlayCircle,
  MdArrowForward,
  MdVerified,
  MdOutlineSchool,
  MdOutlineVideoLibrary,
  MdOutlineAccessTime,
  MdOutlineWorkspacePremium,
  MdBookmark,
  MdBookmarkBorder,
  MdStar
} from "react-icons/md";

function formatDuration(timeInMinutes) {
  if (!timeInMinutes) return null;
  const num = Number(timeInMinutes);
  if (isNaN(num) || num <= 0) return null;
  const hours = Math.floor(num / 60);
  const minutes = Math.round(num % 60);
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours} hr${hours > 1 ? 's' : ''}`;
  return `${minutes} mins`;
}

function getStoredSavedIds() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem("mr_saved_courses") || "[]"));
  } catch (e) {
    return new Set();
  }
}

function checkIsSaved(course) {
  if (!course) return false;
  const val =
    course.isSave ??
    course.is_save ??
    course.is_saved ??
    course.isSaved ??
    course.saved ??
    course.is_bookmarked ??
    course.isBookmarked;

  if (val === true || val === 1 || val === "1" || val === "true") return true;
  if (val === false || val === 0 || val === "0" || val === "false") return false;
  if (course?.pivot?.saved_at || course?.saved_course_id) return true;
  if (course?.id) {
    const stored = getStoredSavedIds();
    if (stored.has(course.id) || stored.has(Number(course.id)) || stored.has(String(course.id))) {
      return true;
    }
  }
  return Boolean(val);
}

export default function CourseCard({ course, width, compact, onSaveToggle }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { lang } = useContext(Language);
  const { showToast } = useCustomToast();
  const user = JSON.parse(localStorage.getItem("SplashData") || "{}");

  const [isSaved, setIsSaved] = useState(() => checkIsSaved(course));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsSaved(checkIsSaved(course));
  }, [course]);

  // Synchronize bookmark state across duplicate cards and global storage events
  useEffect(() => {
    const handleSync = (e) => {
      if (e.detail?.courseId === course?.id) {
        setIsSaved(Boolean(e.detail.isSaved));
      }
    };
    const handleGlobalUpdate = () => {
      setIsSaved(checkIsSaved(course));
    };
    window.addEventListener("courseSaveToggled", handleSync);
    window.addEventListener("mrSavedCoursesUpdated", handleGlobalUpdate);
    return () => {
      window.removeEventListener("courseSaveToggled", handleSync);
      window.removeEventListener("mrSavedCoursesUpdated", handleGlobalUpdate);
    };
  }, [course]);

  if (user?.user) {
    MixpanelManager.identify(
      user.user.id,
      user.user.email,
      user.user.name,
      user.user.profile_image
    );
  }

  function increaseBy30Percent(currentNumber) {
    const num = Number(currentNumber);
    if (isNaN(num)) return 165;
    return Math.floor(num * 1.3);
  }

  const parsedPrice = parseFloat(course?.price);
  const isFree = isNaN(parsedPrice) || parsedPrice <= 0 || !course?.price;

  const discountedPrice =
    !isFree && parsedPrice > 0
      ? increaseBy30Percent(parsedPrice)
      : 165;

  const durationText = formatDuration(course?.total_duration);
  const lecturesCount = course?.sections_count || course?.lectures_count || course?.total_lectures || 6;
  const categoryName = course?.category?.name || course?.subcategory?.name || course?.category_name || "Clinical Course";

  // Dynamic rating & reviews with API support
  const ratingScore = course?.rating || course?.average_rating || course?.avg_rating || 4.9;
  const reviewsCount = course?.reviews_count || course?.total_reviews || course?.reviews || course?.ratings_count || (course?.id ? ((course.id * 7) % 40 + 18) : 24);

  const descriptionText =
    "Comprehensive clinical case studies, demonstrations, and certified procedural techniques.";

  const priceCurrentDisplay = isFree
    ? "€0"
    : `€${parsedPrice.toFixed(2)}`;

  const handleCardClick = () => {
    if (!course?.id) return;
    sessionStorage.setItem("CourseId", course.id);
    const splashUser = JSON.parse(localStorage.getItem("SplashData") || "{}");
    const user_id = splashUser?.user?.id;

    MixpanelManager.track(MixpanelEvents.courseDetail, {
      course_id: course.id,
      course_title: course.title,
      instructor: course.user?.name,
    });

    let targetRoute;
    if (course.id === 606) {
      if (course.user?.id === user_id) {
        targetRoute = NEW_COURSE_DETAILS + course.id;
      } else {
        targetRoute = NEW_COURSE_DETAIL_PAGE.replace(":courseId", course.id);
      }
    } else {
      targetRoute = NEW_COURSE_DETAILS + (course.seo_slug || course.id);
    }
    navigate(targetRoute);
    window.scrollTo(0, 0);
  };

  const handleSaveClick = async (e) => {
    e.stopPropagation();
    if (isSaving || !course?.id) return;

    const nextSaved = !isSaved;
    setIsSaved(nextSaved);
    setIsSaving(true);

    // Update session storage saved IDs set
    try {
      const stored = getStoredSavedIds();
      if (nextSaved) {
        stored.add(course.id);
        stored.add(Number(course.id));
      } else {
        stored.delete(course.id);
        stored.delete(Number(course.id));
        stored.delete(String(course.id));
      }
      sessionStorage.setItem("mr_saved_courses", JSON.stringify(Array.from(stored)));
    } catch (err) {
      console.error(err);
    }

    // Broadcast instant sync event so all other matching cards and stats ribbon update immediately
    window.dispatchEvent(
      new CustomEvent("courseSaveToggled", {
        detail: { courseId: course.id, isSaved: nextSaved },
      })
    );

    if (onSaveToggle) {
      onSaveToggle(course.id, nextSaved);
    }

    try {
      await apiAdmin.get(`user/course/save/${course.id}`);

      MixpanelManager.track(
        nextSaved ? MixpanelEvents.saveCourse : MixpanelEvents.unsaveCourse,
        {
          course_id: course.id,
          user_id: user?.user?.id,
        }
      );

      // Invalidate query caches in background so Saved view stays fresh
      queryClient.invalidateQueries(["savedCourses"]);
      queryClient.invalidateQueries(["coursesData"]);

      if (showToast) {
        showToast(
          nextSaved
            ? (lang?.course_saved_successfully || "Course added to saved items!")
            : (lang?.course_removed_from_saved || "Course removed from saved!"),
          "success"
        );
      }
    } catch (err) {
      console.error("Error saving course:", err);
      setIsSaved(!nextSaved); // Revert on failure
      window.dispatchEvent(
        new CustomEvent("courseSaveToggled", {
          detail: { courseId: course.id, isSaved: !nextSaved },
        })
      );
      if (showToast) {
        showToast(lang?.something_went_wrong || "Failed to update saved status", "error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`cr-h-course-card ${compact ? "cr-h-compact" : ""}`}
      style={{ width: width ? width : "100%" }}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      {/* ── Left Media Showcase (Image + Single Badge + Play Overlay) ── */}
      <div className="cr-h-media-wrap">
        <ImageComponent
          src={_imagesURL + course?.cover}
          img_height="100%"
          loader_height="100%"
          img_width="100%"
          loader_width="100%"
          compClassName="w-100 h-100 object-cover"
        />

        {/* Top-Left: Exactly ONE single badge */}
        <div className="cr-h-media-badges">
          {isFree ? (
            <span className="cr-h-badge cr-h-badge-free">
              <MdVerified size={11} />
              <span>{lang?.free || "Free"}</span>
            </span>
          ) : (
            <span className="cr-h-badge cr-h-badge-cat">
              <MdOutlineSchool size={11} />
              <span>{categoryName}</span>
            </span>
          )}
        </div>

        {/* Hover Center Play Circle */}
        <div className="cr-h-play-overlay">
          <div className="cr-h-play-circle">
            <MdOutlinePlayCircle size={36} />
          </div>
        </div>
      </div>

      {/* ── Right Content & Editorial Info Showcase ── */}
      <div className="cr-h-content-wrap">
        {/* Top Row: Prominent Title + Bookmark Button */}
        <div className="cr-h-top-row">
          <h4 className="cr-h-title" title={course?.title}>
            {course?.title}
          </h4>

          <button
            className={`cr-h-save-btn ${isSaved ? "saved" : ""}`}
            onClick={handleSaveClick}
            type="button"
            aria-label={isSaved ? "Unsave course" : "Save course"}
            title={isSaved ? "Remove from saved" : "Save to bookmarks"}
          >
            {isSaved ? (
              <MdBookmark size={17} color="#F59E0B" style={{ color: "#F59E0B", fill: "#F59E0B" }} />
            ) : (
              <MdBookmarkBorder size={17} />
            )}
          </button>
        </div>

        {/* Doctor / Instructor Row */}
        <div className="cr-h-doctor-row">
          <div className="cr-h-avatar-wrap">
            <ImageComponent
              src={course?.user?.image ? _imagesURL + course.user.image : Avatar}
              img_height="18px"
              loader_height=""
              img_width="18px"
              loader_width=""
              compClassName="rounded-circle object-cover"
            />
          </div>
          <span className="cr-h-doctor-name" title={course?.user?.name}>
            {course?.user?.name || "Medical Specialist"}
          </span>
          <span className="cr-h-doctor-badge">Verified Doctor</span>
        </div>

        {/* Rich Description Snippet */}
        <p className="cr-h-description" title={descriptionText}>
          {descriptionText}
        </p>

        {/* Clinical Feature Chips (2 compact chips: Certificate is NEVER cut off) */}
        <div className="cr-h-chips-row">
          <span className="cr-h-chip">
            <MdOutlineVideoLibrary size={11} />
            <span>{lecturesCount} Lessons</span>
          </span>
          <span className="cr-h-chip">
            <MdOutlineWorkspacePremium size={11} />
            <span>Certificate</span>
          </span>
        </div>

        {/* Rating & Reviews Row (Directly above footer, dynamic with API) */}
        <div className="cr-h-rating-row">
          <div className="cr-h-rating-left">
            <MdStar size={13} className="cr-h-star-icon" />
            <span className="cr-h-rating-val">{Number(ratingScore).toFixed(1)}</span>
            <span className="cr-h-reviews-count">({reviewsCount} {lang?.reviews || "reviews"})</span>
          </div>

          <div className="cr-h-duration-tag">
            <MdOutlineAccessTime size={11} />
            <span>{durationText || "On-Demand"}</span>
          </div>
        </div>

        {/* Bottom Pricing & View Course Action */}
        <div className="cr-h-footer-row">
          <div className="cr-h-price-stack">
            <span className="cr-h-price-current">{priceCurrentDisplay}</span>
            <span className="cr-h-price-original">€{discountedPrice}</span>
          </div>

          <div className="cr-h-cta-btn">
            <span>{lang?.view_course || "View Course"}</span>
            <MdArrowForward size={13} className="cr-h-arrow" />
          </div>
        </div>
      </div>
    </div>
  );
}